import { BriefcaseIcon } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LEVEL_LABEL } from '@/lib/roles';
import type { JobAssignment, JobRole } from '@/lib/types';
import { cn } from '@/lib/utils';

export interface PickableJob {
  id: string;
  name: string;
}

/**
 * Pick the jobs a Project Manager works on and their level on each one. Checking a job assigns
 * `defaultRole`; the per-job select switches between PM Admin (admin) and PM User (user).
 */
export function JobAssignmentPicker({
  jobs,
  value,
  onChange,
  defaultRole,
  disabled,
  lockRole,
  className,
}: {
  jobs: PickableJob[];
  value: JobAssignment[];
  onChange: (next: JobAssignment[]) => void;
  defaultRole: JobRole;
  disabled?: boolean;
  /** Hide the per-job level select (everyone gets `defaultRole`). */
  lockRole?: boolean;
  className?: string;
}) {
  const byId = new Map(value.map((v) => [v.jobId, v.role]));
  const allSelected = jobs.length > 0 && jobs.every((j) => byId.has(j.id));

  const toggle = (jobId: string, on: boolean) => {
    if (on) onChange([...value.filter((v) => v.jobId !== jobId), { jobId, role: byId.get(jobId) ?? defaultRole }]);
    else onChange(value.filter((v) => v.jobId !== jobId));
  };
  const setRole = (jobId: string, role: JobRole) => onChange(value.map((v) => (v.jobId === jobId ? { ...v, role } : v)));

  if (jobs.length === 0) {
    return (
      <div className={cn('text-muted-foreground flex items-center gap-2 border border-dashed px-3 py-4 text-sm', className)}>
        <BriefcaseIcon className="size-4" /> No active jobs yet. Create a job first, then assign people to it.
      </div>
    );
  }

  return (
    <div className={cn('border', className)}>
      <div className="flex items-center justify-between border-b px-3 py-2 text-xs">
        <span className="text-muted-foreground">
          {value.length === 0 ? 'No jobs selected' : `${value.length} of ${jobs.length} ${jobs.length === 1 ? 'job' : 'jobs'} selected`}
        </span>
        <button
          type="button"
          className="text-primary hover:underline disabled:opacity-50"
          disabled={disabled}
          onClick={() => onChange(allSelected ? [] : jobs.map((j) => ({ jobId: j.id, role: byId.get(j.id) ?? defaultRole })))}
        >
          {allSelected ? 'Clear all' : 'Select all'}
        </button>
      </div>
      <ul className="max-h-64 divide-y overflow-y-auto">
        {jobs.map((j) => {
          const role = byId.get(j.id);
          const on = role !== undefined;
          return (
            <li key={j.id} className={cn('flex items-center gap-3 px-3 py-2', on && 'bg-accent/40')}>
              <Checkbox id={`ja-${j.id}`} checked={on} disabled={disabled} onCheckedChange={(v) => toggle(j.id, v === true)} />
              <label htmlFor={`ja-${j.id}`} className="min-w-0 flex-1 cursor-pointer truncate text-sm">
                {j.name}
              </label>
              {on && !lockRole && (
                <Select value={role} onValueChange={(v) => setRole(j.id, v as JobRole)} disabled={disabled}>
                  <SelectTrigger size="sm" className="w-[128px]" aria-label={`Level on ${j.name}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">{LEVEL_LABEL.pm_admin}</SelectItem>
                    <SelectItem value="user">{LEVEL_LABEL.pm_user}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Radio-card picker for the three access levels. */
export function AccessLevelCards<T extends string>({
  options,
  value,
  onChange,
  disabled,
  name = 'level',
}: {
  options: Array<{ value: T; label: string; hint: string }>;
  value: T;
  onChange: (v: T) => void;
  disabled?: boolean;
  name?: string;
}) {
  return (
    <div role="radiogroup" aria-label="Access level" className="grid gap-2 sm:grid-cols-3">
      {options.map((o) => {
        const checked = o.value === value;
        return (
          <label
            key={o.value}
            className={cn(
              'flex cursor-pointer flex-col gap-1 border p-3 text-left transition-colors',
              checked ? 'border-primary bg-accent/50' : 'hover:bg-accent/30',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <span className="flex items-center gap-2">
              <input type="radio" name={name} className="accent-primary" value={o.value} checked={checked} disabled={disabled} onChange={() => onChange(o.value)} />
              <span className="text-sm font-medium">{o.label}</span>
            </span>
            <span className="text-muted-foreground text-xs leading-snug">{o.hint}</span>
          </label>
        );
      })}
    </div>
  );
}
